/**
 * Utility functions for handling timezone conversions and date formatting
 */

import i18next from "i18next";
import { formatTimeOfDay } from "./displayFormat";
import { store } from "../../app/store";
import { selectLastDetectedTimezone } from "../localization/localizationSlice";

/**
 * BCP 47 locale for *display* formatting (dates, times, weekday/month names),
 * derived from the active UI language. ISO/plumbing formatters (e.g. the
 * "en-CA" YYYY-MM-DD tricks) must NOT use this — they rely on a fixed locale.
 */
export const getDisplayLocale = (): string =>
  store.getState().i18n?.currentRegionalCode || "en-US";

/** Translate a relative-day label, falling back to English outside an initialized i18n (unit tests). */
export const relativeDayLabel = (key: string, fallback: string): string =>
  i18next.isInitialized ? i18next.t(key, { defaultValue: fallback }) : fallback;

/**
 * Parse a Date.toString()-like string and extract the date with timezone offset applied.
 * Returns a Date object representing the date in the specified timezone.
 * Example input: "Mon Nov 03 2025 22:00:00 GMT+0200 (Eastern European Standard Time)"
 * Returns: Date object for Nov 4, 2025 (after applying +0200 offset)
 */
export function parseDateWithTimezone(dateStr: string): Date | null {
  // Step 1: Match input string with regex pattern
  const regex =
    /^(?:\w{3})\s(\w{3})\s(\d{1,2})\s(\d{4})\s(\d{2}):(\d{2}):(\d{2})\sGMT([+-]\d{4})/;
  const match = regex.exec(dateStr);

  if (match) {
    const [, monStr, dayStr, yearStr, hourStr, minStr, secStr, gmtOffsetStr] =
      match;

    // Step 2: Month mapping
    const monthMap: Record<string, number> = {
      Jan: 0,
      Feb: 1,
      Mar: 2,
      Apr: 3,
      May: 4,
      Jun: 5,
      Jul: 6,
      Aug: 7,
      Sep: 8,
      Oct: 9,
      Nov: 10,
      Dec: 11,
    };

    const month = monthMap[monStr];

    // Step 3: Convert string components to numbers
    const day = parseInt(dayStr, 10);
    const year = parseInt(yearStr, 10);
    const hour = parseInt(hourStr, 10);
    const minute = parseInt(minStr, 10);
    const second = parseInt(secStr, 10);

    // Step 4: Compute UTC time from components
    const baseUtcMs = Date.UTC(year, month, day, hour, minute, second);

    // Step 5: Handle GMT offset (e.g., +0200 or -0530)
    const sign = gmtOffsetStr.startsWith("-") ? -1 : 1;
    const offsetHours = parseInt(gmtOffsetStr.slice(1, 3), 10);
    const offsetMinutes = parseInt(gmtOffsetStr.slice(3, 5), 10);
    const totalOffsetMinutes = sign * (offsetHours * 60 + offsetMinutes);

    // Step 6: Apply timezone offset to UTC timestamp
    const zonedMs = baseUtcMs + totalOffsetMinutes * 60000;

    const finalDate = new Date(zonedMs);

    return finalDate;
  }

  // Step 7: If regex failed, fallback to native Date parsing with timezone offset addition
  const fallbackDate = new Date(dateStr);

  // Get the timezone offset from the original date and add it to the date
  const timezoneOffsetMinutes = fallbackDate.getTimezoneOffset();

  // Add the timezone offset to the date (getTimezoneOffset returns negative for positive timezones)
  const adjustedTime = fallbackDate.getTime() - timezoneOffsetMinutes * 60000;
  const adjustedTempDate = new Date(adjustedTime);

  // Create a clean date without timezone information using individual components
  const cleanDate = new Date(
    adjustedTempDate.getFullYear(),
    adjustedTempDate.getMonth(),
    adjustedTempDate.getDate(),
    adjustedTempDate.getHours(),
    adjustedTempDate.getMinutes(),
    adjustedTempDate.getSeconds(),
    adjustedTempDate.getMilliseconds()
  );

  return cleanDate;
}

/**
 * Gets the user's current timezone from Redux store or online detection
 * @returns The user's timezone identifier (e.g., "America/New_York", "Europe/London")
 */
export const getUserTimezone = (): string => {
  // First try to get timezone from Redux store (lastDetectedTimezone)
  const state = store.getState();
  const lastDetectedTimezone = selectLastDetectedTimezone(state);

  if (lastDetectedTimezone) {
    return lastDetectedTimezone;
  }

  // Fallback to browser's timezone detection
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn("Failed to detect timezone:", error);
    // Ultimate fallback
    return "UTC";
  }
};

/**
 * Format time in user's timezone with 12-hour format
 * @param utcDateString - UTC date string
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted time string in user's timezone
 */
export const formatTimeInUserTimezone = (
  utcDateString: string,
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = new Date(utcDateString);
  const userTimezone = getUserTimezone();

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    timeZone: userTimezone,
    ...options,
  };

  return new Intl.DateTimeFormat(getDisplayLocale(), defaultOptions).format(date);
};

/**
 * Get date label and display string in user's timezone
 * @param utcDateString - UTC date string
 * @returns Object with label and display strings
 */
export const getDateAndDisplayTimeLabelInUserTimezone = (
  utcDateString: string
): { label: string; display: string } => {
  const userTimezone = getUserTimezone();
  const now = new Date();

  // Parse the input date directly - it should already contain timezone info
  const inputDate = new Date(utcDateString);
  // Get today's date in the user's timezone using Intl.DateTimeFormat
  const todayFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: userTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const todayParts = todayFormatter.formatToParts(now);
  const todayYear = parseInt(
    todayParts.find((p) => p.type === "year")?.value || "0"
  );
  const todayMonth =
    parseInt(todayParts.find((p) => p.type === "month")?.value || "0") - 1; // Month is 0-indexed
  const todayDay = parseInt(
    todayParts.find((p) => p.type === "day")?.value || "0"
  );
  // Get input date in the user's timezone using Intl.DateTimeFormat
  const inputFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: userTimezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const inputParts = inputFormatter.formatToParts(inputDate);
  const inputYear = parseInt(
    inputParts.find((p) => p.type === "year")?.value || "0"
  );
  const inputMonth =
    parseInt(inputParts.find((p) => p.type === "month")?.value || "0") - 1; // Month is 0-indexed

  // Get the timezone offset to determine if we need to adjust the day
  const userTimezoneOffset = getTimezoneOffset(userTimezone, inputDate);

  let inputDay = parseInt(
    inputParts.find((p) => p.type === "day")?.value || "0"
  );

  // If timezone is negative (like Brazil GMT-0300), we might need to add a day
  // This happens when the local time crosses midnight due to timezone conversion
  if (userTimezoneOffset < 0) {
    // For negative timezones, check if we need to add a day
    const originalDateString = inputDate.toString();
    if (
      originalDateString.includes("00:00:00") &&
      originalDateString.includes("GMT-")
    ) {
      inputDay += 1;
    }
  }

  // Create date-only objects for comparison
  const inputDateOnly = new Date(inputYear, inputMonth, inputDay);
  const todayOnly = new Date(todayYear, todayMonth, todayDay);

  const diffTime = inputDateOnly.getTime() - todayOnly.getTime();
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  // For display purposes, create a proper date object in user timezone
  const userDate = new Date(inputYear, inputMonth, inputDay);
  const displayLocale = getDisplayLocale();

  // Check if it's today
  if (diffDays === 0) {
    return {
      label: relativeDayLabel("dates.today", "Today"),
      display: userDate.toLocaleDateString(displayLocale, {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's tomorrow
  if (diffDays === 1) {
    return {
      label: relativeDayLabel("dates.tomorrow", "Tomorrow"),
      display: userDate.toLocaleDateString(displayLocale, {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's yesterday
  if (diffDays === -1) {
    return {
      label: relativeDayLabel("dates.yesterday", "Yesterday"),
      display: userDate.toLocaleDateString(displayLocale, {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // Check if it's within the last week (for past dates) or next week (for future dates)
  if (Math.abs(diffDays) <= 7) {
    return {
      label: userDate.toLocaleDateString(displayLocale, {
        weekday: "short",
      }),
      display: userDate.toLocaleDateString(displayLocale, {
        month: "short",
        day: "numeric",
      }),
    };
  }

  // For older/future dates, show the month abbreviation
  return {
    label: userDate.toLocaleDateString(displayLocale, {
      month: "short",
    }),
    display: userDate.toLocaleDateString(displayLocale, {
      day: "numeric",
    }),
  };
};

/**
 * Format a time range in local format
 * @param startTime - Start time in HH:MM format
 * @param durationMinutes - Duration in minutes
 * @returns Formatted time range string
 */
export const formatLocalTimeRange = (
  startTime: string,
  durationMinutes: number
): string => {
  // Calculate end time
  const [hours, minutes] = startTime.split(":").map(Number);
  const startMinutes = hours * 60 + minutes;
  const endMinutes = startMinutes + durationMinutes;
  const endHours = Math.floor(endMinutes / 60) % 24;
  const endMins = endMinutes % 60;
  const endTime = `${endHours.toString().padStart(2, "0")}:${endMins
    .toString()
    .padStart(2, "0")}`;

  // Format both times
  const formattedStart = formatTime12Hour(startTime);
  const formattedEnd = formatTime12Hour(endTime);

  return `${formattedStart} - ${formattedEnd}`;
};

/**
 * Format a HH:MM time for display in the active locale
 * (en → "9:30 AM", de → "09:30").
 */
const formatTime12Hour = (time: string): string =>
  formatTimeOfDay(time, getDisplayLocale());

/**
 * Convert session time from session timezone to user timezone
 * @param dateStart - Session date (ISO string)
 * @param timeLocal - Session local time (HH:MM format)
 * @param timeZone - Session timezone
 * @param duration - Session duration in minutes
 * @returns Object with UTC datetime, user timezone range, and user timezone date
 */
export const convertSessionTimeToUserTimezone = (
  dateStart: string,
  timeLocal: string,
  timeZone: string,
  duration: number
): {
  utcDateTime: string;
  userTimezoneRange: string;
  userTimezoneDate: Date;
} => {
  try {
    // Parse the session date and time
    const sessionDate = new Date(dateStart);
    const [hours, minutes] = timeLocal.split(":").map(Number);

    // Create a date in the session's timezone using the date and local time
    // We need to construct this as if it's in the session's timezone
    const sessionDateTime = new Date(
      sessionDate.getFullYear(),
      sessionDate.getMonth(),
      sessionDate.getDate(),
      hours,
      minutes
    );

    // Get the timezone offset for the session's timezone at this time
    const sessionTimezoneOffset = getTimezoneOffset(timeZone, sessionDateTime);

    // Calculate the actual UTC time by subtracting the session's timezone offset
    // This converts from session timezone to UTC
    const utcDateTime = new Date(
      sessionDateTime.getTime() - sessionTimezoneOffset * 60000
    );

    // Validate the calculated UTC datetime
    if (isNaN(utcDateTime.getTime())) {
      throw new Error("Invalid UTC datetime calculated");
    }

    // Calculate end time
    const endDateTime = new Date(utcDateTime.getTime() + duration * 60000);

    // Validate the end datetime
    if (isNaN(endDateTime.getTime())) {
      throw new Error("Invalid end datetime calculated");
    }

    // Get user's timezone
    const userTimezone = getUserTimezone();
    // Format start and end times for user's timezone
    const startTimeFormatted = formatTimeInUserTimezone(
      utcDateTime.toISOString()
    );
    const endTimeFormatted = formatTimeInUserTimezone(
      endDateTime.toISOString()
    );

    // Create user timezone date for the session start
    const userTimezoneDate = new Date(
      utcDateTime.toLocaleString("en-US", { timeZone: userTimezone })
    );

    return {
      utcDateTime: utcDateTime.toISOString(),
      userTimezoneRange: `${startTimeFormatted} - ${endTimeFormatted}`,
      userTimezoneDate,
    };
  } catch (error) {
    console.error("Error in convertSessionTimeToUserTimezone:", error, {
      dateStart,
      timeLocal,
      timeZone,
      duration,
    });
    // Fallback: return current time
    const now = new Date();
    const fallbackTime = formatTimeInUserTimezone(now.toISOString());

    return {
      utcDateTime: now.toISOString(),
      userTimezoneRange: `${fallbackTime} - ${fallbackTime}`,
      userTimezoneDate: now,
    };
  }
};

/**
 * Convert a UTC date string to a Date object representing the same instant in the given timezone.
 * @param utcDateString - UTC date string (ISO format)
 * @param timezone - Target timezone identifier (e.g., "America/New_York")
 * @returns Date object adjusted to the target timezone
 */
export function convertUTCToTimezone(
  utcDateString: string,
  timezone: string
): Date {
  const utcDate = new Date(utcDateString);
  const offset = getTimezoneOffset(timezone, utcDate);
  return new Date(utcDate.getTime() + offset * 60000);
}

/**
 * Get timezone offset in minutes for a specific timezone at a given date
 * @param timezone - Target timezone
 * @param date - Reference date
 * @returns Offset in minutes
 */
function getTimezoneOffset(timezone: string, date: Date): number {
  // Create a date formatter for the target timezone
  const targetFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  // Format the date in the target timezone
  const targetParts = targetFormatter.formatToParts(date);
  const targetDateString = `${
    targetParts.find((p) => p.type === "year")?.value
  }-${targetParts.find((p) => p.type === "month")?.value}-${
    targetParts.find((p) => p.type === "day")?.value
  }T${targetParts.find((p) => p.type === "hour")?.value}:${
    targetParts.find((p) => p.type === "minute")?.value
  }:${targetParts.find((p) => p.type === "second")?.value}`;

  // Parse the target date
  const targetDate = new Date(targetDateString);

  // Calculate the offset
  const offset = (targetDate.getTime() - date.getTime()) / (1000 * 60);

  return offset;
}
